import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { BellOff, Check, Loader2, MailWarning } from "lucide-react";

import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface UnsubscribeInfo {
  first_name: string;
  email_masked: string;
  already_unsubscribed: boolean;
}

type View = "loading" | "invalid" | "confirm" | "done" | "cancelled";

/**
 * Public, no-login page reached from the Unsubscribe link in a listing email.
 * The buyer is identified solely by the opaque token in the URL.
 */
export default function UnsubscribePage() {
  const { token } = useParams<{ token: string }>();
  const [view, setView] = useState<View>("loading");
  const [info, setInfo] = useState<UnsubscribeInfo | null>(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    if (!token) {
      setView("invalid");
      return;
    }
    api
      .get<UnsubscribeInfo>(`/unsubscribe/${encodeURIComponent(token)}`)
      .then((data) => {
        if (!active) return;
        setInfo(data);
        setView(data.already_unsubscribed ? "done" : "confirm");
      })
      .catch(() => {
        if (active) setView("invalid");
      });
    return () => {
      active = false;
    };
  }, [token]);

  const handleUnsubscribe = async () => {
    if (!token) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post(`/unsubscribe/${encodeURIComponent(token)}`, {
        comment: comment.trim() || null,
      });
      setView("done");
    } catch (err: unknown) {
      setError(
        err instanceof ApiError ? err.message : "Something went wrong. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted px-4 py-12">
      <Card className="w-full max-w-lg">
        {view === "loading" && (
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <Loader2 className="h-7 w-7 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading your preferences…</p>
          </CardContent>
        )}

        {view === "invalid" && (
          <>
            <CardHeader className="items-center text-center">
              <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 text-amber-600">
                <MailWarning className="h-7 w-7" />
              </div>
              <CardTitle>Link not valid</CardTitle>
              <CardDescription>
                This unsubscribe link is invalid or has expired. If you'd still like to stop
                notification emails, sign in and update your email preferences from your profile.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center">
              <Link to="/sign-in">
                <Button variant="outline" className="rounded-full">Go to sign in</Button>
              </Link>
            </CardContent>
          </>
        )}

        {view === "confirm" && info && (
          <>
            <CardHeader className="items-center text-center">
              <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <BellOff className="h-7 w-7" />
              </div>
              <CardTitle>Unsubscribe from notifications?</CardTitle>
              <CardDescription>
                Hi {info.first_name}, you're about to unsubscribe{" "}
                <span className="font-semibold text-foreground">{info.email_masked}</span> from
                emails about new listings that match your interests. You can turn these back on
                anytime from your Buyer Profile. Your account and all other data stay unchanged.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="unsub-comment" className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">
                  Tell us why (optional)
                </Label>
                <Textarea
                  id="unsub-comment"
                  rows={3}
                  placeholder="Your feedback helps us improve — e.g. too many emails, no longer looking…"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  maxLength={5000}
                  className="resize-none text-sm"
                />
              </div>

              {error && <p className="text-sm font-medium text-destructive">{error}</p>}

              <div className="flex flex-col-reverse gap-3 sm:flex-row">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={() => setView("cancelled")}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  className="flex-1 rounded-full"
                  onClick={handleUnsubscribe}
                  disabled={submitting}
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellOff className="h-4 w-4" />}
                  {submitting ? "Unsubscribing…" : "Unsubscribe"}
                </Button>
              </div>
            </CardContent>
          </>
        )}

        {view === "done" && (
          <>
            <CardHeader className="items-center text-center">
              <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600">
                <Check className="h-7 w-7" />
              </div>
              <CardTitle>You're unsubscribed</CardTitle>
              <CardDescription>
                You'll no longer receive listing notification emails. Changed your mind? You can
                re-enable them anytime from your Buyer Profile after signing in.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center">
              <Link to="/sign-in">
                <Button className="rounded-full">Go to my account</Button>
              </Link>
            </CardContent>
          </>
        )}

        {view === "cancelled" && (
          <>
            <CardHeader className="items-center text-center">
              <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Check className="h-7 w-7" />
              </div>
              <CardTitle>Subscription kept active</CardTitle>
              <CardDescription>
                No changes were made — you'll keep receiving notifications about new listings that
                match your interests.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center">
              <Link to="/">
                <Button variant="outline" className="rounded-full">Return to BusinessLocating</Button>
              </Link>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}
